import { $setBlocksType } from '@lexical/selection';
import {
  $createNodeSelection,
  $createRangeSelection,
  $createTextNode,
  $getNodeByKey,
  $getRoot,
  $getSelection,
  $getState,
  $isElementNode,
  $isNodeSelection,
  $isRangeSelection,
  $isTextNode,
  $setSelection,
  $setState,
  type LexicalNode,
  type NodeKey,
} from 'lexical';

import type {
  DocumentSectionSpec,
  NoteChoiceValuesEdit,
  NoteEditorBlockDef,
} from '../../config';
import {
  $createTemplateHeadingNode,
  $createTemplateParagraphNode,
  $isTemplateFieldNode,
  $isTemplateHeadingNode,
  $isTemplateInstructionNode,
  $isTemplateParagraphNode,
  type TemplateFieldNode,
  type TemplateHeadingNode,
  type TemplateInstructionNode,
  type TemplateParagraphNode,
  templateInstructionRoleState,
} from '../../ui/TemplateEditor/nodes';
import { isTempId } from '../createTempId';

import {
  $createSectionGuidance,
  $createTemplateBlockNode,
  $createTemplateHeading,
  $createTemplateSection,
  $isSectionGuidance,
  $isTemplateSubheading,
  $listTemplateSections,
  $readTemplateLine,
  $readTemplateSection,
  $readTemplateWording,
  $setTemplateLineShowWhen,
  $setTemplateLineSubheading,
  $updateTemplateSection,
} from './templateDocument';
import {
  changeTemplateLinkConditions,
  type TemplateLinkChange,
  type TemplateLinkGraphLine,
} from './templateLinks';

type TemplateBlockNode = TemplateFieldNode | TemplateInstructionNode;

const $isTemplateBlockNode = (
  node: LexicalNode | null | undefined,
): node is TemplateBlockNode =>
  $isTemplateFieldNode(node) || $isTemplateInstructionNode(node);

const $listLines = () =>
  $getRoot().getChildren().filter($isTemplateParagraphNode);

const $findLine = (lineId: string) =>
  $listLines().find((line) => $readTemplateLine(line)?.ID === lineId);

const $findHeading = (sectionId: string) =>
  $getRoot()
    .getChildren()
    .filter($isTemplateHeadingNode)
    .find((heading) => $readTemplateSection(heading)?.ID === sectionId);

const $findBlockNode = (blockId: string) =>
  $listLines()
    .flatMap((line) => line.getChildren())
    .filter($isTemplateBlockNode)
    .find((node) => node.getBlockId() === blockId);

// The heading or paragraph a node is in.
const $findTopBlock = (node: LexicalNode | null | undefined) =>
  node ? node.getTopLevelElement() ?? null : null;

// The section's heading and lines, as they stand in the document.
const $findSectionNodes = (sectionId: string) =>
  $listTemplateSections().find(
    ({ heading }) =>
      !!heading && $readTemplateSection(heading)?.ID === sectionId,
  );

const $readNodeBlock = (key: NodeKey) => {
  const node = $getNodeByKey(key);
  return $isTemplateBlockNode(node) ? node : null;
};

export const $setTemplateBlockDef = (key: NodeKey, def: NoteEditorBlockDef) =>
  $readNodeBlock(key)?.setDef(def);

export const $removeTemplateBlock = (key: NodeKey) =>
  $readNodeBlock(key)?.remove();

// The values the links of every line name on one block, mapped: a value
// mapped to null drops, and a condition left without values goes, as scribe
// refuses an empty one.
export const $mapTemplateLinkValues = (
  blockId: string,
  map: (value: string) => string | null,
) =>
  $listLines().forEach((line) => {
    const showWhen = $readTemplateLine(line)?.ShowWhen ?? [];
    if (!showWhen.some((condition) => condition.BlockID === blockId)) {
      return;
    }
    $setTemplateLineShowWhen(
      line,
      showWhen.flatMap((condition) => {
        if (condition.BlockID !== blockId) {
          return [condition];
        }
        const Values = [
          ...new Set(condition.Values.flatMap((value) => map(value) ?? [])),
        ];
        return Values.length ? [{ ...condition, Values }] : [];
      }),
    );
  });

const withValues = (
  def: NoteEditorBlockDef,
  Values: string[],
): NoteEditorBlockDef => {
  switch (def.Kind.case) {
    case 'Choice':
      return {
        ...def,
        Kind: { case: 'Choice', value: { ...def.Kind.value, Values } },
      };
    case 'MultiChoice':
      return {
        ...def,
        Kind: { case: 'MultiChoice', value: { ...def.Kind.value, Values } },
      };
    default:
      return def;
  }
};

const readValues = (def: NoteEditorBlockDef) =>
  def.Kind.case === 'Choice' || def.Kind.case === 'MultiChoice'
    ? def.Kind.value.Values
    : [];

const applyValuesEdit = (values: string[], edit: NoteChoiceValuesEdit) => {
  switch (edit.type) {
    case 'add':
      return [...values, edit.value];
    case 'rename':
      return values.map((value, index) =>
        index === edit.index ? edit.value : value,
      );
    case 'remove':
      return values.filter((_, index) => index !== edit.index);
    default: {
      const moved = values[edit.from];
      const rest = values.filter((_, index) => index !== edit.from);
      return [...rest.slice(0, edit.to), moved, ...rest.slice(edit.to)];
    }
  }
};

// A choice's values edited in its field; the links that name a renamed
// value follow it, and those that name a removed one drop it.
export const $editTemplateOptions = (
  key: NodeKey,
  edit: NoteChoiceValuesEdit,
) => {
  const node = $readNodeBlock(key);
  if (!node) {
    return;
  }
  const def = node.getDef();
  const values = readValues(def);
  node.setDef(withValues(def, applyValuesEdit(values, edit)));
  if (edit.type === 'rename' && values[edit.index] !== edit.value) {
    const from = values[edit.index];
    $mapTemplateLinkValues(def.ID, (value) =>
      value === from ? edit.value : value,
    );
  }
  if (edit.type === 'remove') {
    const removed = values[edit.index];
    $mapTemplateLinkValues(def.ID, (value) =>
      value === removed ? null : value,
    );
  }
};

export const $updateTemplateSectionById = (
  sectionId: string,
  patch: Partial<DocumentSectionSpec>,
) => {
  const heading = $findHeading(sectionId);
  if (heading) {
    $updateTemplateSection(heading, patch);
  }
};

const $removeNodes = (nodes: LexicalNode[]) => {
  const selection = $getSelection();
  const held = selection
    ?.getNodes()
    .some((node) =>
      nodes.some((removed) => removed.is(node) || removed.isParentOf(node)),
    );
  if (held) {
    $setSelection(null);
  }
  nodes.forEach((node) => node.remove());
  if ($getRoot().isEmpty()) {
    $getRoot().append($createTemplateParagraphNode());
  }
};

export const $removeTemplateSection = (sectionId: string) => {
  const section = $findSectionNodes(sectionId);
  if (section?.heading) {
    $removeNodes([section.heading, ...section.body]);
  }
};

// The caret goes to the start of a line; its node's key, or null.
export const $selectTemplateLine = (lineId: string) => {
  const line = $findLine(lineId);
  line?.selectStart();
  return line?.getKey() ?? null;
};

// The caret goes to the end of a section's title; the heading's key, or
// null.
export const $selectTemplateSection = (sectionId: string) => {
  const heading = $findHeading(sectionId);
  heading?.selectEnd();
  return heading?.getKey() ?? null;
};

// The caret goes to the start of the line a choice's value is in; that
// line's key, or null.
export const $selectTemplateValue = (blockId: string) => {
  const line = $findTopBlock($findBlockNode(blockId));
  line?.selectStart();
  return line?.getKey() ?? null;
};

// The paragraph the caret is in, else the one it was last in.
const $readCaretLine = (fallback: NodeKey | null) => {
  const selection = $getSelection();
  const at =
    $isRangeSelection(selection) || $isNodeSelection(selection)
      ? $findTopBlock(selection.getNodes()[0])
      : $getNodeByKey(fallback ?? '');
  return $isTemplateParagraphNode(at) || $isTemplateHeadingNode(at) ? at : null;
};

export const $readCaretBlockKey = () => $readCaretLine(null)?.getKey() ?? null;

// The section the caret's line or heading is in.
const $readCaretSection = (fallback: NodeKey | null) => {
  const at = $readCaretLine(fallback);
  return at
    ? $listTemplateSections().find(
        ({ heading, body }) =>
          at.is(heading) || body.some((node) => node.is(at)),
      )
    : undefined;
};

// A space keeps a field from the word it is put against: the word takes it,
// so it stays the word's block; between two fields it stands alone.
const needsSpaceBefore = (text: string) => !!text && !/\s$/.test(text);

const needsSpaceAfter = (text: string) =>
  !!text && !/^[\s.,;:!?…%)]/.test(text);

const $spaceAround = (node: LexicalNode) => {
  const previous = node.getPreviousSibling();
  const next = node.getNextSibling();
  if ($isTextNode(previous)) {
    const before = previous.getTextContent();
    if (needsSpaceBefore(before)) {
      previous.setTextContent(`${before} `);
    }
  } else if (previous) {
    node.insertBefore($createTextNode(' '));
  }
  if ($isTextNode(next)) {
    const after = next.getTextContent();
    if (needsSpaceAfter(after)) {
      next.setTextContent(` ${after}`);
    }
  } else if (next) {
    node.insertAfter($createTextNode(' '));
  }
};

// Whether the line holds no text before the node.
const $startsLine = (node: LexicalNode) => {
  const before = node.getPreviousSiblings();
  return before.every(
    (sibling) => $isTextNode(sibling) && !sibling.getTextContent().trim(),
  );
};

// A field put where its line has no text before it gets a name to type
// over, selected.
const $nameField = (node: LexicalNode, name: string) => {
  node.getPreviousSiblings().forEach((sibling) => sibling.remove());
  const text = $createTextNode(`${name}: `);
  node.insertBefore(text);
  text.select(0, name.length);
};

// A field or an instruction put at the caret, in the caret's line, or after
// the field selected; a heading or a subheading takes it in a new line
// under it. The caret goes after it, or onto the name given a field at the
// start of its line (`fieldName`). The node's key, and whether it was named.
export const $insertTemplateBlockAtCaret = (
  def: NoteEditorBlockDef,
  fallback: NodeKey | null,
  fieldName = '',
) => {
  const node = $createTemplateBlockNode(def);
  const selection = $getSelection();
  const found = $readCaretLine(fallback);
  const at = $isTemplateSubheading(found) ? null : found;
  const [selected] = $isNodeSelection(selection) ? selection.getNodes() : [];
  if ($isRangeSelection(selection) && $isTemplateParagraphNode(at)) {
    selection.insertNodes([node]);
    $spaceAround(node);
  } else if ($isTemplateBlockNode(selected) && $isTemplateParagraphNode(at)) {
    selected.insertAfter(node);
    $spaceAround(node);
  } else {
    const line = $createTemplateParagraphNode().append(node);
    if (found) {
      found.insertAfter(line);
    } else {
      $getRoot().append(line);
    }
  }
  const named = !!fieldName && $isTemplateFieldNode(node) && $startsLine(node);
  if (named) {
    $nameField(node, fieldName);
  } else {
    node.selectNext(0, 0);
  }
  return { key: node.getKey(), named };
};

// The selected text of one line or heading, in one line.
export const $readSelectedTemplateText = () => {
  const selection = $getSelection();
  return $isRangeSelection(selection)
    ? selection.getTextContent().replace(/\s+/g, ' ').trim()
    : '';
};

// The whole title of a heading just added is selected: what is typed
// replaces it.
const $selectTitle = (heading: TemplateHeadingNode) => {
  const text = heading.getFirstChild();
  if ($isTextNode(text)) {
    text.select(0, text.getTextContentSize());
  } else {
    heading.selectEnd();
  }
};

// A new section's heading: the caret's empty line becomes it, else it goes
// under the caret's line or heading and takes the lines after it.
export const $insertTemplateHeadingAtCaret = (
  section: DocumentSectionSpec,
  fallback: NodeKey | null,
) => {
  const heading = $createTemplateHeading(section);
  const at = $readCaretLine(fallback);
  if ($isTemplateParagraphNode(at) && at.isEmpty()) {
    at.replace(heading);
  } else if (at) {
    at.insertAfter(heading);
  } else {
    $getRoot().append(heading);
  }
  $selectTitle(heading);
};

// The caret's line becomes a section's heading, or, while empty, a new
// section whose title is ready to type.
export const $turnCaretIntoTemplateHeading = (
  section: DocumentSectionSpec,
  fallback: NodeKey | null,
) => {
  const at = $readCaretLine(fallback);
  if ($isTemplateHeadingNode(at)) {
    return;
  }
  if ($isTemplateParagraphNode(at) && !at.isEmpty()) {
    $setBlocksType($getSelection(), $createTemplateHeadingNode);
    return;
  }
  $insertTemplateHeadingAtCaret(section, fallback);
};

// The caret's line or heading becomes a subheading of its section, or a
// plain line; «Text» in a plain line adds an empty line after it, the caret
// in it.
export const $turnCaretIntoTemplateLine = (
  subheading: boolean,
  fallback: NodeKey | null,
) => {
  const at = $readCaretLine(fallback);
  if ($isTemplateHeadingNode(at)) {
    $setBlocksType($getSelection(), $createTemplateParagraphNode);
  }
  const line = $readCaretLine(fallback);
  if (!$isTemplateParagraphNode(line)) {
    return;
  }
  if (subheading || $isTemplateSubheading(line) || $isTemplateHeadingNode(at)) {
    $setTemplateLineSubheading(line, subheading);
    return;
  }
  line.insertAfter($createTemplateParagraphNode()).selectStart();
};

// A field of a summary or an evaluation added after the caret's field, its
// title ready to type and an empty instruction under it.
export const $insertTemplateFieldSection = (
  section: DocumentSectionSpec,
  fallback: NodeKey | null,
) => {
  const owner = $readCaretSection(fallback);
  const last = owner ? owner.body.at(-1) ?? owner.heading : null;
  const nodes = $createTemplateSection(section, true);
  if (last) {
    nodes.reduce<LexicalNode>((before, node) => before.insertAfter(node), last);
  } else {
    $getRoot().append(...nodes);
  }
  const [heading] = nodes;
  if ($isTemplateHeadingNode(heading)) {
    $selectTitle(heading);
  }
};

// The caret's field of a summary or an evaluation gets its instruction in
// brackets under its title: the one it holds, or one made of the text typed
// there. The instruction's block id, or null when the caret is in no field.
export const $bracketTemplateFieldInstruction = (fallback: NodeKey | null) => {
  const field = $readCaretSection(fallback);
  if (!field?.heading) {
    return null;
  }
  const lines = field.body.filter($isTemplateParagraphNode);
  const held = lines
    .flatMap((line) => line.getChildren())
    .find($isSectionGuidance);
  if (held) {
    return held.getBlockId();
  }
  const guidance = $createSectionGuidance(
    lines
      .map((line) => line.getTextContent())
      .join('\n')
      .trim(),
  );
  lines.forEach((line) => line.remove());
  field.heading.insertAfter($createTemplateParagraphNode().append(guidance));
  return guidance.getBlockId();
};

// The blocks a line holds, its text blocks among them.
const $listLineBlockIds = (line: TemplateParagraphNode) => [
  ...new Set(
    line.getChildren().flatMap((node) => {
      if ($isTemplateBlockNode(node)) {
        return [node.getBlockId()];
      }
      const id = $readTemplateWording(node)?.def.ID;
      return id ? [id] : [];
    }),
  ),
];

// The lines in the selection, or the caret's line; a subheading is never
// linked.
export const $readSelectedTemplateLineIds = (): string[] => {
  const selection = $getSelection();
  if (!$isRangeSelection(selection) && !$isNodeSelection(selection)) {
    return [];
  }
  const lines = [
    ...selection.getNodes().map($findTopBlock),
    ...($isRangeSelection(selection)
      ? [$findTopBlock(selection.anchor.getNode())]
      : []),
  ]
    .filter($isTemplateParagraphNode)
    .filter((line) => !$isTemplateSubheading(line));
  return [
    ...new Set(
      lines.flatMap((line) => {
        const id = $readTemplateLine(line)?.ID;
        return id ? [id] : [];
      }),
    ),
  ];
};

// Every line, the blocks it holds and the values that open it.
export const $readTemplateLinkGraph = (): TemplateLinkGraphLine[] =>
  $listLines().flatMap((line) => {
    const read = $readTemplateLine(line);
    return read
      ? [
          {
            lineId: read.ID,
            blockIds: $listLineBlockIds(line),
            showWhen: read.ShowWhen.flatMap((condition) =>
              condition.Values.map((value) => ({
                blockId: condition.BlockID,
                value,
              })),
            ),
          },
        ]
      : [];
  });

// Adds an option that opens the lines, removes one or replaces one, each
// line keeping its other options (`changeTemplateLinkConditions`). They
// stay where they are.
export const $changeTemplateLinesOpeners = (
  lineIds: string[],
  change: TemplateLinkChange,
) =>
  lineIds.forEach((lineId) => {
    const line = $findLine(lineId);
    if (line) {
      $setTemplateLineShowWhen(
        line,
        changeTemplateLinkConditions(
          $readTemplateLine(line)?.ShowWhen ?? [],
          change,
        ),
      );
    }
  });

// The first line under a heading, holding nothing but the node, in a
// section with no guidance yet: where a section's guidance stands.
const $isGuidanceSpot = (node: LexicalNode) => {
  const line = node.getParent();
  const heading = line?.getPreviousSibling();
  if (!$isTemplateParagraphNode(line) || !$isTemplateHeadingNode(heading)) {
    return false;
  }
  const section = $listTemplateSections().find((item) =>
    item.heading?.is(heading),
  );
  const guided = section?.body.some(
    (other) =>
      $isTemplateParagraphNode(other) &&
      other
        .getChildren()
        .some((child) => !child.is(node) && $isSectionGuidance(child)),
  );
  return (
    !guided &&
    line
      .getChildren()
      .every(
        (child) =>
          child.is(node) ||
          ($isTextNode(child) && !child.getTextContent().trim()),
      )
  );
};

// The field right before a node, a space between them aside.
const $readFieldBefore = (node: LexicalNode) => {
  const previous = node.getPreviousSibling();
  const field =
    $isTextNode(previous) && !previous.getTextContent().trim()
      ? previous.getPreviousSibling()
      : previous;
  return $isTemplateFieldNode(field) ? field : null;
};

// A new instruction takes its role from where it stands: alone on the first
// line under a heading it is the section's guidance; with text, right after
// a field that has none, it becomes that field's AI instruction, shown in
// the field; anywhere else it is a slot. Instructions a template was read
// with keep theirs.
export const $placeTemplateInstruction = (node: TemplateInstructionNode) => {
  if (
    !isTempId(node.getBlockId()) ||
    $getState(node, templateInstructionRoleState) === 'section'
  ) {
    return;
  }
  if ($isGuidanceSpot(node)) {
    $setState(node, templateInstructionRoleState, 'section');
    return;
  }
  const text = node.getDef().AIInstruction?.trim();
  const field = $readFieldBefore(node);
  if (text && field && !field.getDef().AIInstruction?.trim()) {
    field.setDef({ ...field.getDef(), AIInstruction: text });
    node.remove();
  }
};

// A field selected as one unit, as Backspace next to it does first.
export const $selectTemplateBlock = (key: NodeKey) => {
  const selection = $createNodeSelection();
  selection.add(key);
  $setSelection(selection);
};

// The caret right after a field or an instruction, as when its own text box
// is left.
export const $selectAfterTemplateBlock = (key: NodeKey) => {
  const node = $readNodeBlock(key);
  if (!node) {
    return;
  }
  const next = node.getNextSibling();
  if ($isTextNode(next)) {
    next.select(0, 0);
    return;
  }
  const parent = node.getParent();
  if ($isElementNode(parent)) {
    const offset = node.getIndexWithinParent() + 1;
    const selection = $createRangeSelection();
    selection.anchor.set(parent.getKey(), offset, 'element');
    selection.focus.set(parent.getKey(), offset, 'element');
    $setSelection(selection);
  }
};
